
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  YesNoTypeSchema,
  mapExperienceOnboardingFormForToFrontend,
  mapYourExperienceOnboardingFormForBackend,
  yourExperienceOnboardingFormSchema
} from "./types";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button, Form, FormField, FormInput, FormLabel, Textarea } from "../ui";
import { ActivityIndicator, View } from "react-native";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Recruitment } from "../types/Recruitment";
import { trpc } from "@/trpc/react";
import { router } from "expo-router";
import Toast from "react-native-toast-message";

interface YourExperienceFormProps {
  recruitmentData: Recruitment
  onFormSuccess: () => void
}

export default function YourExperienceForm({ recruitmentData, onFormSuccess }: YourExperienceFormProps) {

  const insets = useSafeAreaInsets();
  const contentInsets = {
    top: insets.top,
    bottom: insets.bottom,
    left: 12,
    right: 12,
  }

  const form = useForm<z.infer<typeof yourExperienceOnboardingFormSchema>>({
    resolver: zodResolver(yourExperienceOnboardingFormSchema),
    defaultValues: mapExperienceOnboardingFormForToFrontend(recruitmentData)
  });

  const { mutate: updateRecruitment } = trpc.recruitment.update.useMutation({
    onSuccess(_response) {
      onFormSuccess();
      router.back();
      Toast.show({
        type: 'success',
        text1: 'Successfully saved your experience details'
      });
    },
  });

  async function onSubmit(data: z.infer<typeof yourExperienceOnboardingFormSchema>) {
    const mappedDetails = mapYourExperienceOnboardingFormForBackend(recruitmentData.id, data);
    updateRecruitment(mappedDetails);
  }

  return (
    <Card className='mt-4 mb-20'>
      <CardContent>
        <Form {...form}>
          <View className="gap-4 mt-8">
            <FormField
              control={form.control}
              name="lead_details"
              render={({ field }) => (
                <View className='flex-1'>
                  <FormLabel>Where did you hear about us?</FormLabel>
                  <Textarea
                    {...field}
                    onChangeText={(value) => {
                      field.onChange(value);
                    }}
                    value={field.value || ''}
                  />
                </View>
              )}
            />
            <View>
              <FormLabel>Do you have a current UK Driving License?</FormLabel>
              <FormField
                control={form.control}
                name="driving_license"
                render={({ field }) => (

                  <Select onValueChange={(option) => field.onChange(option?.value || '')} value={{ value: field.value || "", label: field.value || "" }}>
                    <SelectTrigger className='w-[250px]'>
                      <SelectValue
                        className='text-foreground text-sm native:text-lg'
                        placeholder='Select an option'
                      />
                    </SelectTrigger>
                    <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                      <SelectGroup>
                        {YesNoTypeSchema.options.map((type, index) => (
                          <SelectItem key={index} label={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </View>
            {form.watch("driving_license") === "Yes" && (
              <>
                <FormField
                  control={form.control}
                  name="driving_license_endorsements"
                  render={({ field }) => (
                    <View className='flex-1'>
                      <FormLabel>If there are any endorsements, please provide details</FormLabel>
                      <Textarea
                        {...field}
                        onChangeText={(value) => {
                          field.onChange(value);
                        }}
                        value={field.value || ''}
                      />
                    </View>
                  )}
                />
                <View>
                  <FormLabel>Vehicle Owner?</FormLabel>
                  <FormField
                    control={form.control}
                    name="vehicle_owner"
                    render={({ field }) => (
                      <Select onValueChange={(option) => field.onChange(option?.value || '')} value={{ value: field.value || "", label: field.value || "" }}>
                        <SelectTrigger className='w-[250px]'>
                          <SelectValue
                            className='text-foreground text-sm native:text-lg'
                            placeholder='Select an option'
                          />
                        </SelectTrigger>
                        <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                          <SelectGroup>
                            {YesNoTypeSchema.options.map((type, index) => (
                              <SelectItem key={index} label={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </View>
              </>
            )}
            <View>
              <FormLabel>Do you have any other specialist licenses (Cherry picker, Forklift, etc.)?</FormLabel>
              <FormField
                control={form.control}
                name="specialist_license"
                render={({ field }) => (

                  <Select onValueChange={(option) => field.onChange(option?.value || '')} value={{ value: field.value || "", label: field.value || "" }}>
                    <SelectTrigger className='w-[250px]'>
                      <SelectValue
                        className='text-foreground text-sm native:text-lg'
                        placeholder='Select an option'
                      />
                    </SelectTrigger>
                    <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                      <SelectGroup>
                        {YesNoTypeSchema.options.map((type, index) => (
                          <SelectItem key={index} label={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </View>
            {form.watch("specialist_license") === "Yes" && (
              <View>
                <FormField
                  control={form.control}
                  name="specialist_license_type"
                  render={({ field }) => (
                    <FormInput
                      label="What type(s) of specialist license(s) do you have?"
                      {...field}
                    />
                  )}
                />
              </View>
            )}

            <View>
              <FormLabel>Do you currently, or have you ever provided services for another crewing company?</FormLabel>
              <FormField
                control={form.control}
                name="crew_services"
                render={({ field }) => (

                  <Select onValueChange={(option) => field.onChange(option?.value || '')} value={{ value: field.value || "", label: field.value || "" }}>
                    <SelectTrigger className='w-[250px]'>
                      <SelectValue
                        className='text-foreground text-sm native:text-lg'
                        placeholder='Select an option'
                      />
                    </SelectTrigger>
                    <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                      <SelectGroup>
                        {YesNoTypeSchema.options.map((type, index) => (
                          <SelectItem key={index} label={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </View>
            {form.watch("crew_services") === "Yes" && (
              <FormField
                control={form.control}
                name="crew_services_details"
                render={({ field }) => (
                  <View className='flex-1'>
                    <FormLabel>Please provide details of the crewing company you have worked for and the level you worked at.</FormLabel>
                    <Textarea
                      {...field}
                      onChangeText={(value) => {
                        field.onChange(value);
                      }}
                      value={field.value || ''}
                    />
                  </View>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="skills_description"
              render={({ field }) => (
                <View className='flex-1'>
                  <FormLabel>
                    Provide details of any skills, experience or qualifications you have acquired that can support this application.
                    Either within or outside of the crewing industry.
                  </FormLabel>
                  <Textarea
                    {...field}
                    onChangeText={(value) => {
                      field.onChange(value);
                    }}
                    value={field.value || ''}
                  />
                </View>
              )}
            />
            <FormField
              control={form.control}
              name="team_description"
              render={({ field }) => (
                <View className='flex-1'>
                  <FormLabel>
                    Provide us with an example of when you have had to work as part of a team
                  </FormLabel>
                  <Textarea
                    {...field}
                    onChangeText={(value) => {
                      field.onChange(value);
                    }}
                    value={field.value || ''}
                  />
                </View>
              )}
            />
          </View>
          <Button
            size="default"
            variant="crew"
            onPress={form.handleSubmit(onSubmit)}
            className="mt-4"
          >
            {form.formState.isSubmitting ? (
              <ActivityIndicator size="small" />
            ) : (
              "Save"
            )}
          </Button>
        </Form>
      </CardContent>
    </Card >
  )

}